// How to connect: what the app's developers need, read-only, every value with
// a copy button. Endpoints from POST /api/v1/idp/settings (internal/idp/idp.go
// EndpointsFor). OpenID Connect and MFA API apps open with "Connect your app"
// (the AuthNull URL or Discovery URL, the client ID and, for an app with a
// secret, its hint and Rotate secret); the other URLs are under
// "API endpoints (for developers)".
import React, { useEffect, useState } from "react";
import { Button, Collapse, Spin, message } from "antd";
import { BookOpen, Download, Info, TriangleAlert } from "lucide-react";

import { CountChip } from "../../design-system/tableUi";
import { AN_COLORS } from "../../design-system/tokens";
import { getIdpSettings, listAppAssignments } from "../../service/idpService";
import { CodeBlock, FieldLabel, InlineValue, Note, PanelHead, card } from "../enduser/ssh-proxy/sshProxyUi";
import { HelpText, RotateSecretButton, SecretHint, ValueRow, hasRotatableSecret, reportIdpError, settingsBlock } from "./appsUi";

const MFA_API_GUIDE = "https://github.com/authnull0/authnull-service/blob/main/docs/MFA-API.md";

// The IdP signing certificate, from the SAML metadata, as a PEM file.
const downloadCertificate = async (metadataUrl) => {
  const res = await fetch(metadataUrl, { credentials: "omit" });
  if (!res.ok) throw new Error(`The metadata answered ${res.status}.`);
  const text = await res.text();
  const m = text.match(/<(?:\w+:)?X509Certificate>([\s\S]*?)<\/(?:\w+:)?X509Certificate>/);
  const body = (m ? m[1] : "").replace(/\\[nrt]/g, "").replace(/\s+/g, "");
  if (!body || !/^[A-Za-z0-9+/=]+$/.test(body)) throw new Error("No signing certificate in the metadata.");
  const lines = body.match(/.{1,64}/g) || [];
  const pem = `-----BEGIN CERTIFICATE-----\n${lines.join("\n")}\n-----END CERTIFICATE-----\n`;
  const url = URL.createObjectURL(new Blob([pem], { type: "application/x-pem-file" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "authnull-idp-signing.pem";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const rows = (list) => list.map((r, i) => <ValueRow key={r.label} first={i === 0} {...r} />);

// The client secret row: its hint, when it was made, and Rotate secret. The
// secret itself is never shown here.
function SecretRow({ app, issuer, onRotated }) {
  return (
    <div style={{ padding: "10px 0", borderTop: `1px solid ${AN_COLORS.line2}` }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(8rem, 12rem) minmax(0, 1fr) auto", gap: 14, alignItems: "center" }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>Client secret</div>
        <SecretHint oidc={app.oidc} />
        <RotateSecretButton app={app} issuer={issuer} onRotated={onRotated} size="small" />
      </div>
      <HelpText>
        The secret is shown only once, when it's created or rotated. If it's lost, rotate it and update the app. It
        can't be shown again: AuthNull keeps only a hash, like Azure.
      </HelpText>
    </div>
  );
}

// One numbered setup step.
function Step({ n, children }) {
  return (
    <li style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <span
        style={{
          width: "1.375rem",
          height: "1.375rem",
          borderRadius: "50%",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: AN_COLORS.bg,
          border: `1px solid ${AN_COLORS.line}`,
          color: AN_COLORS.ink2,
          fontSize: 11.5,
          fontWeight: 600,
        }}
      >
        {n}
      </span>
      <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: AN_COLORS.ink2, lineHeight: 1.55, paddingTop: 1 }}>{children}</div>
    </li>
  );
}

// The other URLs, collapsed.
function EndpointsSection({ list }) {
  return (
    <div style={{ ...card, padding: "4px 6px" }}>
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: "endpoints",
            label: <span style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>API endpoints (for developers)</span>,
            children: rows(list),
          },
        ]}
      />
    </div>
  );
}

const linkButton = { padding: 0, height: "auto", fontSize: 13 };

export default function HowToConnect({ app, active = true, onSecretRotated, onOpenAssignments }) {
  const [msg, msgCtx] = message.useMessage();
  const [endpoints, setEndpoints] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Users + groups assigned to an MFA API app; null while unknown.
  const [assigned, setAssigned] = useState(null);

  const isMfaApi = app?.protocol === "mfa_api";
  const appId = app?.id;
  // Read again each time the tab is shown: assignments change on their tab.
  useEffect(() => {
    if (!isMfaApi || !appId || !active) return undefined;
    let alive = true;
    listAppAssignments(appId)
      .then((res) => alive && setAssigned((res?.assignments || []).length))
      .catch(() => alive && setAssigned(null));
    return () => {
      alive = false;
    };
  }, [isMfaApi, appId, active]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getIdpSettings()
      .then((res) => alive && setEndpoints(res?.endpoints || {}))
      .catch((err) => {
        if (!alive) return;
        setFailed(true);
        reportIdpError(err, { msg, fallback: "Could not read the sign-in endpoints." });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [msg]);

  if (loading) {
    return (
      <div style={{ ...card, padding: 40, textAlign: "center" }}>
        {msgCtx}
        <Spin />
      </div>
    );
  }
  const e = endpoints || {};
  const clientId = app?.oidc?.clientId || "";

  let body;
  if (app?.protocol === "saml") {
    const download = () => {
      setDownloading(true);
      downloadCertificate(e.samlMetadataUrl)
        .catch((err) => msg.error(err?.message || "Could not download the certificate."))
        .finally(() => setDownloading(false));
    };
    body = (
      <>
        <PanelHead title="SAML" desc="Give these to the app's SAML settings." />
        {rows([
          { label: "IdP metadata URL", hint: "Most SAML apps need only this", value: e.samlMetadataUrl },
          { label: "IdP entity ID", hint: "Issuer", value: e.samlEntityId },
          { label: "Single sign-on URL", hint: "HTTP-Redirect or HTTP-POST", value: e.samlSsoUrl },
        ])}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(8rem, 12rem) minmax(0, 1fr)",
            gap: 14,
            alignItems: "center",
            padding: "10px 0",
            borderTop: `1px solid ${AN_COLORS.line2}`,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>Signing certificate</div>
          <div>
            <Button
              size="small"
              icon={<Download size={13} style={{ verticalAlign: -2 }} />}
              loading={downloading}
              disabled={!e.samlMetadataUrl}
              onClick={download}
            >
              Download certificate
            </Button>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <Note tone="info" icon={<Info size={15} />}>
            In your app's SAML settings, paste the metadata URL, or enter the entity ID, SSO URL and certificate. AuthNull
            signs both the response and the assertion (RSA-SHA256). The sign-in must start from your app (SP-initiated).
          </Note>
        </div>
      </>
    );
  } else {
    const secret = hasRotatableSecret(app);
    const base = e.issuer ? `${e.issuer}/mfa` : "";
    const connect = (
      <div style={{ ...card, padding: 18 }}>
        <PanelHead
          title="Connect your app"
          desc={
            isMfaApi
              ? "Server to server, over HTTPS. Never put the client secret in a browser or desktop client."
              : "Give these to the app's developers."
          }
        />
        {rows([
          isMfaApi
            ? { label: "AuthNull URL", value: e.issuer }
            : { label: "Discovery URL", hint: "Most apps need only this", value: e.discoveryUrl },
          { label: "Client ID", value: clientId },
        ])}
        {secret && <SecretRow app={app} issuer={e.issuer} onRotated={onSecretRotated} />}
      </div>
    );

    if (isMfaApi) {
      const env = settingsBlock({ issuer: e.issuer, clientId, secret: "<the secret you copied when it was created>" });
      body = (
        <>
          {connect}
          <div style={{ ...card, padding: 18, display: "grid", gap: 14 }}>
            <div>
              <FieldLabel>App settings</FieldLabel>
              <CodeBlock multiline copyText={env}>
                {env}
              </CodeBlock>
            </div>
            <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
              <Step n={1}>Give these three values to the app's developers, or enter them in the app's settings.</Step>
              <Step n={2}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span>
                    Assign the users who will use MFA (
                    <Button type="link" style={linkButton} onClick={onOpenAssignments}>
                      Assignments tab
                    </Button>
                    ).
                  </span>
                  {assigned != null && <CountChip count={assigned} label="assigned" />}
                </div>
                {assigned === 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Note tone="warn" icon={<TriangleAlert size={15} />}>
                      No one is assigned yet. AuthNull refuses MFA for every user until you assign them. This is the
                      most common setup mistake.
                    </Note>
                  </div>
                )}
              </Step>
              <Step n={3}>
                The app calls AuthNull after its own password check.{" "}
                <Button
                  type="link"
                  style={linkButton}
                  icon={<BookOpen size={13} style={{ verticalAlign: -2 }} />}
                  href={MFA_API_GUIDE}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Integration guide
                </Button>
              </Step>
            </ol>
          </div>
          <EndpointsSection
            list={[
              { label: "Token endpoint", hint: "Client credentials grant", value: e.tokenEndpoint },
              { label: "API base", value: base },
              { label: "Start a challenge", hint: "POST", value: base && `${base}/challenges` },
              { label: "Verify a code", hint: "POST", value: base && `${base}/verify` },
            ]}
          />
        </>
      );
    } else {
      body = (
        <>
          {connect}
          <EndpointsSection
            list={[
              { label: "Issuer", value: e.issuer },
              { label: "Authorization", value: e.authorizationEndpoint },
              { label: "Token", value: e.tokenEndpoint },
              { label: "Userinfo", value: e.userinfoEndpoint },
              { label: "JWKS", value: e.jwksUrl },
              { label: "Revocation", value: e.revocationEndpoint },
              { label: "Sign-out", value: e.endSessionEndpoint },
            ]}
          />
          <Note tone="info" icon={<Info size={15} />}>
            Your app's developers add an OpenID Connect login using the Discovery URL and Client ID. Use the authorization
            code flow with PKCE.
            {app?.oidc?.tokenEndpointAuthMethod === "none" ? (
              <> This app is a public client: it has no secret.</>
            ) : (
              <>
                {" "}
                The app authenticates at the token endpoint with its client secret (
                <InlineValue>{app?.oidc?.tokenEndpointAuthMethod || "client_secret_basic"}</InlineValue>).
              </>
            )}
          </Note>
        </>
      );
    }
  }

  if (failed && !endpoints) {
    return (
      <div style={{ ...card, padding: 18, maxWidth: "56rem" }}>
        {msgCtx}
        <Note tone="danger">The sign-in endpoints could not be read. Reload the page to try again.</Note>
      </div>
    );
  }
  if (app?.protocol === "saml") {
    return (
      <div style={{ ...card, padding: 18, maxWidth: "56rem" }}>
        {msgCtx}
        {body}
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 16, maxWidth: "56rem" }}>
      {msgCtx}
      {body}
    </div>
  );
}
